// src/utils/backgroundPresets.ts
  import { BackgroundVariant } from '@xyflow/react';
import {
    PulsePatternClass,
    FloatingPatternClass,
    GlitchPatternClass,
    BreathingPatternClass,
    WavePatternClass,
    ShimmerPatternClass,
    RotatePatternClass,
    MatrixPatternClass,
  } from '@/styles/animations.css';
  
  export interface BackgroundPreset {
    id: string;
    name: string;
    background: string;
    color: string;
    gap?: number | [number, number];
    variant?: BackgroundVariant;
    lineWidth?: number;
    size?: number;
    offset?: number | [number, number];
    patternClassName?: string;
    animated?: boolean;
  }
  
  export const backgroundPresets: BackgroundPreset[] = [
    // Standard Themes
    {
      id: 'default',
      name: 'Default',
      background: 'linear-gradient(#fffa0010,#00fafa10)',
      color: '#ff000033',
      gap: 50,
      variant: BackgroundVariant.Cross,
    },
    {
      id: 'blueprint',
      name: 'Blueprint',
      background: 'linear-gradient(#0a192f, #172a46)',
      color: '#4d8aff33',
      gap: 40,
      variant: BackgroundVariant.Lines,
      lineWidth: 0.5,
    },
    {
      id: 'forest',
      name: 'Forest',
      background: 'linear-gradient(#1a3a1a, #2d5a2d)',
      color: '#7fff7f33',
      gap: 60,
      variant: BackgroundVariant.Cross,
      size: 4,
    },
    {
      id: 'sunset',
      name: 'Sunset',
      background: 'linear-gradient(#ff8c42, #7d56c9)',
      color: '#ffcc5c33',
      gap: 45,
      variant: BackgroundVariant.Dots,
      size: 2,
    },
    {
      id: 'grayscale',
      name: 'Grayscale',
      background: 'linear-gradient(#f5f5f5, #e0e0e0)',
      color: '#33333320',
      gap: 30,
      variant: BackgroundVariant.Cross,
      size: 3,
    },
    {
      id: 'dark',
      name: 'Dark',
      background: 'linear-gradient(#1a1a1a, #333333)',
      color: '#ffffff20',
      gap: 35,
      variant: BackgroundVariant.Dots,
      size: 1,
    },
    {
      id: 'arctic',
      name: 'Arctic',
      background: 'linear-gradient(#e8f4ff, #c9e6ff)',
      color: '#3b82f620',
      gap: 50,
      variant: BackgroundVariant.Cross,
      lineWidth: 0.75,
    },
    {
      id: 'desert',
      name: 'Desert',
      background: 'linear-gradient(#f5e1c0, #e2c196)',
      color: '#d4943e30',
      gap: 55,
      variant: BackgroundVariant.Dots,
      size: 2,
    },
    {
      id: 'ocean',
      name: 'Ocean',
      background: 'linear-gradient(#004466, #006699)',
      color: '#00ccff25',
      gap: 40,
      variant: BackgroundVariant.Lines,
      lineWidth: 1,
    },
    {
      id: 'violet',
      name: 'Violet',
      background: 'linear-gradient(#4a148c, #7b1fa2)',
      color: '#e040fb30',
      gap: 45,
      variant: BackgroundVariant.Cross,
      size: 4,
    },
  
    // Specialized Themes
    {
      id: 'graph-paper',
      name: 'Graph Paper',
      background: '#ffffff',
      color: '#2195f320',
      gap: 20,
      variant: BackgroundVariant.Lines,
      lineWidth: 0.5,
    },
    {
      id: 'circuit',
      name: 'Circuit Board',
      background: '#0f2027',
      color: '#50fa7b30',
      gap: [60, 60],
      variant: BackgroundVariant.Cross,
      size: 2,
      lineWidth: 0.75,
    },
    {
      id: 'nebula',
      name: 'Nebula',
      background: 'linear-gradient(#090030, #200060)',
      color: '#ff00ff15',
      gap: 40,
      variant: BackgroundVariant.Dots,
      size: 1.5,
    },
    {
      id: 'matrix',
      name: 'Matrix',
      background: '#000000',
      color: '#00ff0025',
      gap: 15,
      variant: BackgroundVariant.Lines,
      lineWidth: 1,
    },
    {
      id: 'retro-grid',
      name: 'Retro Grid',
      background: 'linear-gradient(#fc466b, #3f5efb)',
      color: '#ffffff20',
      gap: 25,
      variant: BackgroundVariant.Lines,
      lineWidth: 1,
    },
    {
      id: 'pixel-art',
      name: 'Pixel Art',
      background: '#272736',
      color: '#feeb7710',
      gap: 15,
      variant: BackgroundVariant.Cross,
      size: 1,
    },
    {
      id: 'blueprint-dots',
      name: 'Blueprint Dots',
      background: '#1a2b3c',
      color: '#00bfff25',
      gap: 25,
      variant: BackgroundVariant.Dots,
      size: 1,
    },
    {
      id: 'neon',
      name: 'Neon',
      background: '#16161d',
      color: '#ff00ff30',
      gap: 40,
      variant: BackgroundVariant.Cross,
      size: 3,
    },
    {
      id: 'minimal-dots',
      name: 'Minimal Dots',
      background: '#ffffff',
      color: '#22222210',
      gap: 80,
      variant: BackgroundVariant.Dots,
      size: 0.8,
    },
    {
      id: 'high-contrast',
      name: 'High Contrast',
      background: '#000000',
      color: '#ffffff40',
      gap: 50,
      variant: BackgroundVariant.Cross,
      size: 5,
    },
  
    // Animated Themes
    {
      id: 'pulse',
      name: 'Pulse',
      background: 'linear-gradient(#2b5876, #4e4376)',
      color: '#eeeeee50',
      gap: 50,
      variant: BackgroundVariant.Dots,
      size: 2,
      patternClassName: 'pulse-pattern',
      animated: true,
    },
    {
      id: 'floating',
      name: 'Floating',
      background: 'linear-gradient(#134e5e, #71b280)',
      color: '#ffffff30',
      gap: 60,
      variant: BackgroundVariant.Cross,
      size: 4,
      patternClassName: 'floating-pattern',
      animated: true,
    },
    {
      id: 'glitch',
      name: 'Glitch',
      background: 'linear-gradient(#000000, #1a1a2e)',
      color: '#ff073a30',
      gap: 30,
      variant: BackgroundVariant.Lines,
      lineWidth: 1,
      patternClassName: 'glitch-pattern',
      animated: true,
    },
    {
      id: 'breathing',
      name: 'Breathing',
      background: 'linear-gradient(#0b2545, #13315c)',
      color: '#8da9c440',
      gap: 50,
      variant: BackgroundVariant.Cross,
      size: 3,
      patternClassName: 'breathing-pattern',
      animated: true,
    },
    {
      id: 'wave',
      name: 'Ocean Waves',
      background: 'linear-gradient(#01497c, #012a4a)',
      color: '#89c2d940',
      gap: 40,
      variant: BackgroundVariant.Dots,
      size: 1.5,
      patternClassName: 'wave-pattern',
      animated: true,
    },
    {
      id: 'shimmer',
      name: 'Shimmer',
      background: 'linear-gradient(#240046, #3c096c)',
      color: '#9d4edd40',
      gap: 35,
      variant: BackgroundVariant.Dots,
      size: 1,
      patternClassName: 'shimmer-pattern',
      animated: true,
    },
    {
      id: 'rotate',
      name: 'Orbit',
      background: 'linear-gradient(#001219, #005f73)',
      color: '#94d2bd30',
      gap: 45,
      variant: BackgroundVariant.Cross,
      size: 2,
      patternClassName: 'rotate-pattern',
      animated: true,
    },
    {
      id: 'matrix',
      name: 'Matrix Rain',
      background: '#0a0e0a',
      color: '#00ff4460',
      gap: 25,
      variant: BackgroundVariant.Dots,
      size: 1,
      patternClassName: 'matrix-pattern',
      animated: true,
    },
    {
      id: 'cyberpunk',
      name: 'Cyberpunk',
      background: 'linear-gradient(#1a1a2e, #16213e)',
      color: '#ff00ff30',
      gap: 30,
      variant: BackgroundVariant.Lines,
      lineWidth: 1,
      patternClassName: 'shimmer-pattern',
      animated: true,
    },
    {
      id: 'cosmic',
      name: 'Cosmic Drift',
      background: 'linear-gradient(#0f0524, #1a0536)',
      color: '#f7258530',
      gap: [40, 40],
      variant: BackgroundVariant.Dots,
      size: 1.2,
      patternClassName: 'wave-pattern',
      animated: true,
    },
    {
      id: 'neon-pulse',
      name: 'Neon Pulse',
      background: 'linear-gradient(#6600ff, #00ffcc)',
      color: '#ffffff60',
      gap: 50,
      variant: BackgroundVariant.Dots,
      size: 2.5,
      patternClassName: 'pulse-pattern',
      animated: true,
    },
    {
      id: 'sunset-wave',
      name: 'Sunset Wave',
      background: 'linear-gradient(#ff6b6b, #ffe66d)',
      color: '#ff447550',
      gap: 40,
      variant: BackgroundVariant.Dots,
      size: 2,
      patternClassName: 'wave-pattern',
      animated: true,
    },
    {
      id: 'electric-shimmer',
      name: 'Electric Shimmer',
      background: 'linear-gradient(#00c9ff, #92fe9d)',
      color: '#0066cc50',
      gap: 35,
      variant: BackgroundVariant.Cross,
      size: 3,
      patternClassName: 'shimmer-pattern',
      animated: true,
    },
    {
      id: 'candy-rotate',
      name: 'Candy Swirl',
      background: 'linear-gradient(#fc5c7d, #6a82fb)',
      color: '#ffffff55',
      gap: 45,
      variant: BackgroundVariant.Cross,
      size: 2.5,
      patternClassName: 'rotate-pattern',
      animated: true,
    },
    {
      id: 'citrus-breathe',
      name: 'Citrus Breathe',
      background: 'linear-gradient(#ffcc33, #ff6633)',
      color: '#ffffff60',
      gap: 50,
      variant: BackgroundVariant.Cross,
      size: 3,
      patternClassName: 'breathing-pattern',
      animated: true,
    },
    {
      id: 'digital-matrix',
      name: 'Digital Matrix',
      background: 'linear-gradient(#0575e6, #021b79)',
      color: '#00ff8870',
      gap: 25,
      variant: BackgroundVariant.Dots,
      size: 1.2,
      patternClassName: 'matrix-pattern',
      animated: true,
    },
    {
      id: 'aurora-glitch',
      name: 'Aurora Glitch',
      background: 'linear-gradient(#43cea2, #185a9d)',
      color: '#ccff0060',
      gap: 30,
      variant: BackgroundVariant.Lines,
      lineWidth: 1,
      patternClassName: 'glitch-pattern',
      animated: true,
    },
  ]